import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import _find from 'lodash/find';

import { UnderlinedButton } from '@components/shared/buttons/underlined-button/UnderlinedButton';
import { useCatalogData } from '@contexts/catalog-data';
import { useIsMobile } from '@contexts/mobile-detector-provider';
import { UAH } from '@lib/constants/common-constants';
import Chip from '@page-layouts/catalog/TopBar/Chip';
import { SEFUrlsService } from '@services';
import { IFilterProp, IFiltersJSON } from '@types';
import { ATTRIBUTE_COLOR_PATH } from '../FiltersBar/options/constants';
import { filterStringToJson } from '../FiltersBar/utils';
import { SortingSelect } from './SortingSelect';

import classes from './TopBar.module.scss';

const TopBar = () => {
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { catalog } = useCatalogData();
  const isMobile = useIsMobile();

  const filterParams: IFiltersJSON | undefined = catalog
    ? filterStringToJson(SEFUrlsService.getQueryFromUrl(pathName, searchParams.toString()), catalog.filters)
    : undefined;

  const isFilterExists = (path: string, value: string): boolean => {
    try {
      if (path.startsWith('attributes.')) {
        const attributeAlias = path.split('.')[1];
        const attributeFilter = _find(catalog ? catalog?.filters?.attributes : null, { alias: attributeAlias });
        if (attributeFilter && attributeFilter.options && attributeFilter.options.length) {
          return Boolean(attributeFilter.options.find((a) => a.value === value));
        }
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  function handleResetFilters() {
    router.push(`/${SEFUrlsService.baseUrl}`);
  }

  const handleDelete = (item: IFilterProp) => () => {
    const newFilters =
      filterParams?.arrayRes?.filter((i) => i.path !== item.path || (i.path === item.path && i.value !== item.value)) ||
      [];

    router.push(SEFUrlsService.getUrl(newFilters, filterParams));
  };

  const setSort = (newSort: string) => {
    const params = new URLSearchParams(searchParams.toString());
    const sort = newSort.split('_')[0];
    const sortOrder = newSort.split('_')[1];
    params.set('sortBy', sort);
    if (sortOrder) {
      params.set('sortOrder', sortOrder);
    } else {
      params.delete('sortOrder');
    }
    const url = params.toString();
    router.push(`${pathName}?${url}`);
  };

  const activeFilters = filterParams?.arrayRes?.filter((item) => item.path !== 'searchText') || [];

  return (
    <aside className={classes.wrapperTopBar}>
      <div className={clsx(classes.topBarFiltersWrapper, 'with-margin')}>
        <div className={classes.selectedFilters}>
          <div className={classes.chipList}>
            {activeFilters.map((item, index: number) => {
              if (item.path === 'instock' || item.path === 'preorder') return null;
              let isDisabled = isFilterExists(item.path, item.value);

              let label = item.label || item.value;
              if (
                item.path === 'height' ||
                item.path === 'diameter' ||
                item.path === 'width' ||
                item.path === 'length'
              ) {
                label += ' см';
              }
              if (item.path === 'volume') {
                label += ' см3';
              }
              if (item.path === 'packing') {
                label += ' шт';
              }
              if (item.path === 'weight') {
                label += ' г';
              }
              if (item.path === 'price' || item.path === 'priceRange' || item.path === 'pricerange') {
                label += ` ${UAH}/шт`;
              }
              if (item.path === ATTRIBUTE_COLOR_PATH && item.value === '') {
                isDisabled = false;
              }

              if (!label) {
                return null;
              }

              return <Chip onDelete={handleDelete(item)} isDisabled={isDisabled} label={label} key={index} />;
            })}
            {activeFilters.length ? (
              <UnderlinedButton className={classes.resetAllButton} onClick={handleResetFilters} label="Скинути все" />
            ) : null}
          </div>
        </div>
        <div className={classes.sortOrder}>{!isMobile ? <SortingSelect setFilter={setSort} /> : null}</div>
      </div>
    </aside>
  );
};

export default TopBar;
